import React, { useState, useRef, MouseEvent, useEffect } from 'react';

import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';

import { useRecoilValue, useRecoilState } from 'recoil';

import { DeleteSweep, Download, FactorySharp, Language as LanguageIcon, RemoveCircle, TranslateOutlined as TranslateOutlinedIcon, } from '@mui/icons-material';
import RestartAltIcon from '@mui/icons-material/RestartAlt'; // for reset

import { BsGlobe } from 'react-icons/bs';
import { IoChevronUpOutline } from 'react-icons/io5';
import { FaDatabase, FaFileDownload, FaFileExport, FaFileImport, FaFileUpload } from 'react-icons/fa';
import { TbLayoutDashboard } from 'react-icons/tb';

import { Link } from 'react-router-dom';

import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';

import Popper from '@mui/material/Popper';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import { useLocalStorage } from 'react-use';

import ClickAwayListener from '@mui/material/ClickAwayListener';

import { flexBetweenCenter, fullWidthFlex, justifyCenter } from 'themes/commonStyles';

import { isExportBoxShowAtom, isDesktopPublishingBoxShowAtom, currentUserSessionAtom, currentFormNameAtom, useSideBarMenuAtom } from 'library/store';
import { MdOutlineSwapHoriz } from 'react-icons/md';
import { FormDialog } from './ui/FormDialog';
import EnhancedTable, { ActionIconTableRow } from './ui/EnhancedTable';
import { IFile } from 'library/interface';

import entityService, { useBasicFilterEntity } from 'features/services/Entity';
import SplitButton from './ui/SplitButton';
import { UploadFormDialog } from './UploadFormDialog';
import { DownloadFormDialog } from './DownloadFormDialog';


import { globalConfig } from "config";


export const Footer = () => {

  const anchorRefLanguageButton = useRef(null);
  const [openLanguageMenu, setOpenLanguageMenu] = useState(false);

  const [isExportBoxShow, setIsExportBoxShow] = useRecoilState(isExportBoxShowAtom);

  const [useSideBarMenu, setUseSideBarMenu] = useRecoilState(useSideBarMenuAtom);
  const [isDesktopPublishingBoxShow, setIsDesktopPublishingBoxShow] = useRecoilState(isDesktopPublishingBoxShowAtom);

  const currentFormName = useRecoilValue(currentFormNameAtom);

  const [currentUserSession, setCurrentUserSession] = useRecoilState(currentUserSessionAtom);

  const [storeUseSideBarMenu, setStoreUseSideBarMenu, removeStoreUseSideBarMenu] = useLocalStorage<boolean>('UseSideBarMenu');

  const { createDbBackup, getBackupFiles, resetBackupFiles, removeBackupFile,
    getLogFiles, downloadLogFile, resetLogFiles, getReportLogFiles, downloadReportLogFile, resetReportLogFiles } = entityService();

  const [openLogs, setOpenLogs] = useState<boolean>(false);
  const [logFiles, setLogFiles] = useState<IFile[]>([]);

  const [reportLogFiles, setReportLogFiles] = useState<IFile[]>([]);

  const [openBackups, setOpenBackups] = useState<boolean>(false);
  const [backupFiles, setBackupFiles] = useState<IFile[]>([]);

  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const footerLinks: { id: number, text: string, url: string }[] = [
    // { id: 1, text: t('Privacy policy'), url: '#' },
    // { id: 2, text: t('Terms of use'), url: '#' },
  ];


  const [openUploadForm, setOpenUploadForm] = useState<boolean>(false);
  const [openAppUpdateDownloadForm, setOpenAppUpdateDownloadForm] = useState<boolean>(false);

  const supportDropdownItems = [
    {
      label: t('Upload file'), icon: <FaFileUpload fontSize="small" />,
      onClick: () => setOpenUploadForm(true)
    },

    {
      label: t('Download application update'), icon: <FaFileDownload fontSize="small" />,
      onClick: () => setOpenAppUpdateDownloadForm(true)
    },

    ...(globalConfig.get().superUsers.includes( `-${currentUserSession.userId}-`) ? [
      {
        label: t('Database backup'),
        icon: <FaDatabase fontSize="small" />,
        onClick: async () => {
          const arr = await getBackupFiles();
          setBackupFiles(arr);
          setOpenBackups(true);
        },
      }
    ] : [])

    
  ];

  const menuLanguageItemClick = (lg: string) => (event: any) => {
    i18n.changeLanguage(lg);
    setOpenLanguageMenu(false);
    setCurrentUserSession({ ...currentUserSession, language: lg });
  }

  const onClickLanguageMenu = () => {
    setOpenLanguageMenu(true);
  };

  const closeLanguageMenu = () => {
    setOpenLanguageMenu(false);
  };

  const toggleLayout = async (event: MouseEvent<HTMLButtonElement>) => {
    const isSideBarMenu = !useSideBarMenu;
    setUseSideBarMenu(isSideBarMenu);
    setStoreUseSideBarMenu(isSideBarMenu);
  }

  const handleSupportClick = async (/*event : MouseEvent<HTMLButtonElement>*/) => {
    const arr = await getLogFiles();
    setLogFiles(arr);

    const arrReport = await getReportLogFiles();
    setReportLogFiles(arrReport);

    setOpenLogs(true);
  }

  const logFileRowActionIcon = (file: IFile): ActionIconTableRow<IFile, IFile> => {
    const res: ActionIconTableRow<IFile, IFile> = {
      toolTip: t('Action'),
      icon: Download,
      hasAction: (index: number, row: IFile) => {
        return true; 
      }, 
      isActionExecuting: true,
      onRowClickIcon: (event: any, index: number, row: IFile) => {
        downloadLogFile(row.fileName);
      }
    }
    return res;
  }

  const reportLogFileRowActionIcon = (file: IFile): ActionIconTableRow<IFile, IFile> => {
    const res: ActionIconTableRow<IFile, IFile> = {
      toolTip: t('Action'),
      icon: Download,
      hasAction: (index: number, row: IFile) => {
        return true; 
      }, 
      isActionExecuting: true,
      onRowClickIcon: (event: any, index: number, row: IFile) => {
        downloadReportLogFile(row.fileName);
      }
    }
    return res;
  }

  const backupFileRowActionIcon = (file: IFile): ActionIconTableRow<IFile, IFile> => {

    const res: ActionIconTableRow<IFile, IFile> = {
      toolTip: t('Action'),
      icon: RemoveCircle,
      hasAction: (index: number, row: IFile) => {

        //const roleEntityStudent = roleEntities.find(re => re.entityName === 'Student');
        return true; //!isFalsy(roleEntityStudent);
      }, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: async (event: any, index: number, row: IFile) => {
        await removeBackupFile(row.fileName);

        const arr = await getBackupFiles();
        setBackupFiles(arr);
      }
    }
    return res;
  }

  const handleResetLog = async (event: any) => {
    await resetLogFiles();

    const arr = await getLogFiles();
      setLogFiles(arr);
  }

  const handleResetReportLog = async (event: any) => {
    await resetReportLogFiles();

    const arr = await getReportLogFiles();
      setReportLogFiles(arr);
  }

  const handleResetBackup = async (event: any) => {
    await resetBackupFiles();

    const arr = await getBackupFiles();
    setBackupFiles(arr);
  }

  const [basicImportAllowed, setBasicImportAllowed] = useState<boolean>(false);
  const handleImportClick = async (event: MouseEvent<HTMLButtonElement>) => {

    try {
      //setSaveExecuting(true);
      event.preventDefault();
      const btn = document.getElementById(`btnBasicImport`);
      if (btn == null) {
        enqueueSnackbar('bad action ...', {
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
        });
        return;
      }

      await btn?.click();

    } finally {
      //setSaveExecuting(false);
    }
  }

  const [isBackupGoingOn, setIsBackupGoingOn] = useState<boolean>(false);

  const handleDbBackup = async (event: MouseEvent<HTMLButtonElement>) => {

    try {
      setIsBackupGoingOn(true);
      await createDbBackup();

      const arr = await getBackupFiles();
      setBackupFiles(arr);

    } finally {
      setIsBackupGoingOn(false);
    }
  }


  useEffect(() => {
    setBasicImportAllowed(document.getElementById(`btnBasicImport`) !== null);
  }, [currentFormName]);

  return (
    <Box
      sx={{
        ...fullWidthFlex,
        borderTop: '1px solid #ddd',
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            ...flexBetweenCenter,
            width: '100%',
          }}
        >
          <Stack>
            <Paper>
              <Link to={'https://www.univ-soft.com'}> 2022 Copyright </Link>
            </Paper>
            {footerLinks.map((link) => {
              return (
                <Paper key={link.id} sx={{ paddingLeft: '16px' }}>
                  <Link to={link.url}> {link.text}</Link>
                </Paper>
              );
            })}
          </Stack>

          <Stack>
            <Paper sx={justifyCenter}>
              <Button ref={anchorRefLanguageButton} onClick={onClickLanguageMenu}>
                <Box sx={{ ...justifyCenter, mr: 1 }}>
                  <BsGlobe size={24} />
                </Box>
              </Button>
              <Popper open={openLanguageMenu} anchorEl={anchorRefLanguageButton.current} role={undefined} transition >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
                  >
                    <Paper>
                      <ClickAwayListener onClickAway={closeLanguageMenu}>
                        <MenuList dense={true} id="menu-list-grow">
                          {(i18n.language !== 'fr') &&
                            <MenuItem onClick={menuLanguageItemClick("fr")}>
                              <ListItemIcon>
                                <TranslateOutlinedIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary="Francais" />
                            </MenuItem>}
                          {(i18n.language !== 'en') &&
                            <MenuItem onClick={menuLanguageItemClick("en")}>
                              <ListItemIcon>
                                <TranslateOutlinedIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary="English" />
                            </MenuItem>}
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
              {basicImportAllowed && <Button onClick={handleImportClick}>
                <Box sx={{ ...justifyCenter, ml: 1 }}>
                  <FaFileImport size={24} />
                </Box>
                {t('Import')}
              </Button>}
              {currentUserSession.isExtractAndExportAllowed && <Button onClick={() => { setIsDesktopPublishingBoxShow(true); }}>
                <Box sx={{ ...justifyCenter, ml: 1 }}>
                  <TbLayoutDashboard size={24} />
                </Box>
                {t('Desktop publishing')}
              </Button>}
              {currentUserSession.isExtractAndExportAllowed && <Button onClick={() => { setIsExportBoxShow(true); }}>
                <Box sx={{ ...justifyCenter, ml: 1 }}>
                  <FaFileExport size={24} />
                </Box>
                {t('Export')}
              </Button>}
              <Button onClick={toggleLayout}>
                <Box sx={{ ...justifyCenter, ml: 1, alignItems: 'center' }}>
                  <MdOutlineSwapHoriz size={24} />
                </Box>
                {t('Switch Layout')}
              </Button>
              <SplitButton
                label={t('Support and Resources')}
                onClick={handleSupportClick}
                dropdownItems={supportDropdownItems}
                color="primary"
                variant="text"
              />
              {/* <Button onClick={handleSupportClick}>                
                <Box sx={{ ...justifyCenter, ml: 1, alignItems: 'center', }}>
                  <IoChevronUpOutline size={24} />
                </Box>
                {t('Support and Resources')}
              </Button> */}
            </Paper>
            {openUploadForm && <UploadFormDialog open={openUploadForm} onClose={() => { setOpenUploadForm(false); }} />}
            {openAppUpdateDownloadForm && <DownloadFormDialog open={openAppUpdateDownloadForm}
              onClose={() => { setOpenAppUpdateDownloadForm(false); }}
              entityId={0} entityName='Upload' />}
            {openLogs && <FormDialog open={openLogs} maxWidth='md'
              okText='Ok' cancelText={t('Cancel')} title={t('Parameters')} onCancel={() => { setOpenLogs(false); }}
              onClose={() => { setOpenLogs(false); }} onOk={() => { setOpenLogs(false); }}  >
              <Stack flexDirection='column'  >
                <Box sx={{ mt: 1, width: '100%' }} >
                  <EnhancedTable<IFile>
                    rows={logFiles}
                    headCells={[
                      { id: 'fileName', label: t('Name'), display: true, type: 'string', },
                      { id: 'creationTime', label: t('Creation'), display: true, type: 'date' },
                      { id: 'lastWriteTime', label: t('Last write'), display: true, type: 'date', },
                      { id: 'sizeKb', label: `${t('Size')} (kb<->ko)`, display: true, type: 'numeric', },
                    ]}
                    title={`${t('Log files')} #${logFiles.length}`}
                    objKey='fileName' stateSelected={undefined} onRowSelected={undefined} rowCheckedMode='single'
                    onRowCheckedSelectChange={undefined} order='desc' orderBy='lastWriteTime'
                    onRowDoubleClick={undefined}
                    rowActionIcon={logFileRowActionIcon}
                    toolbarActions={[
                      { toolTip: `${t('Reset')}...---`, onClickIcon: handleResetLog, icon: RestartAltIcon, },
                    ]}
                  />
                </Box>

                <Box sx={{ mt: 1, width: '100%' }} >
                  <EnhancedTable<IFile>
                    rows={reportLogFiles}
                    headCells={[
                      { id: 'fileName', label: t('Name'), display: true, type: 'string', },
                      { id: 'creationTime', label: t('Creation'), display: true, type: 'date' },
                      { id: 'lastWriteTime', label: t('Last write'), display: true, type: 'date', },
                      { id: 'sizeKb', label: `${t('Size')} (kb<->ko)`, display: true, type: 'numeric', },
                    ]}
                    title={`${t('Log files')} - ${t('Reports')}  #${reportLogFiles.length}`}
                    objKey='fileName' stateSelected={undefined} onRowSelected={undefined} rowCheckedMode='single'
                    onRowCheckedSelectChange={undefined} order='desc' orderBy='lastWriteTime'
                    onRowDoubleClick={undefined}
                    rowActionIcon={reportLogFileRowActionIcon}
                    toolbarActions={[
                      { toolTip: `${t('Reset')}...---`, onClickIcon: handleResetReportLog, icon: RestartAltIcon, },
                    ]}
                  />
                </Box>
              </Stack>
            </FormDialog>}
            {openBackups && <FormDialog open={openBackups} maxWidth='md'
              okText='Ok' cancelText={t('Cancel')} title={t('Parameters')} onCancel={() => { setOpenBackups(false); }}
              onClose={() => { setOpenBackups(false); }} onOk={() => { setOpenBackups(false); }}  >
              <Stack flexDirection='column'  >
                <Box sx={{ mt: 4, width: '100%' }} >

                  <Button fullWidth variant="contained" disabled={isBackupGoingOn} onClick={handleDbBackup}>
                    {`${t('Create backup')}...`}
                  </Button>
                </Box>
                <Box sx={{ mt: 1, width: '100%' }} >
                  <EnhancedTable<IFile>
                    rows={backupFiles}
                    headCells={[
                      { id: 'fileName', label: t('Name'), display: true, type: 'string', },
                      { id: 'creationTime', label: t('Creation'), display: true, type: 'date' },
                      { id: 'lastWriteTime', label: t('Last write'), display: true, type: 'date', },
                      { id: 'sizeKb', label: `${t('Size')} (kb<->ko)`, display: true, type: 'numeric', },
                    ]}
                    title={`${t('Backup files')} ${backupFiles.length}`}
                    objKey='fileName' stateSelected={undefined} onRowSelected={undefined} rowCheckedMode='single'
                    onRowCheckedSelectChange={undefined} order='desc' orderBy='lastWriteTime'
                    onRowDoubleClick={undefined}
                    rowActionIcon={backupFileRowActionIcon}
                    toolbarActions={[
                      { toolTip: `${t('Reset')}...---`, onClickIcon: handleResetBackup, icon: RestartAltIcon, },
                    ]}
                  />
                </Box>
              </Stack>
            </FormDialog>}
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}
